/**
 * @file Header.jsx
 * @description Modern responsive header with Tailwind CSS
 */

import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Link, useLocation } from 'react-router-dom';
import { menuItems } from '../Menu';
import IMAGES from '../config/images';

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-md sticky top-0 z-50 transition-all duration-300">
      {/* Emergency Banner */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-2 text-center text-sm animate-pulse-slow">
        <span className="font-semibold">🚨 24/7 Emergency Service Available - Call Now: </span>
        <a href="tel:+447379787766" className="underline hover:text-red-200 transition-colors">07379 787766</a>
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo/Brand */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 group"
          >
            <img
              src={IMAGES.logo}
              alt="Metro Plumbing & Heating"
              className="h-16 w-auto transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div 
              style={{ display: 'none' }} 
              className="flex flex-col items-start"
            >
              <h3 className="text-xl font-bold text-primary-600 leading-tight">
                Metro Plumbing
              </h3>
              <span className="text-sm text-gray-600">& Heating</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {menuItems.map((menu) => (
              <Link
                key={menu.label}
                to={menu.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 relative
                  ${isActive(menu.path)
                    ? 'text-primary-600 bg-primary-50'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                  }`}
              >
                {menu.label}
                {isActive(menu.path) && (
                  <span className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 bg-primary-600 animate-fade-in"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <IconButton
            onClick={toggleDrawer(true)}
            className="md:hidden"
            sx={{ color: '#1976d2' }}
          >
            <MenuIcon />
          </IconButton>
        </div>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          },
        }}
      >
        <Box className="h-full flex flex-col bg-gradient-to-br from-primary-600 to-primary-800">
          {/* Drawer Header */}
          <div className="p-4 flex items-center justify-between border-b border-white/20">
            <h3 className="text-white font-bold text-lg">Menu</h3>
            <IconButton onClick={toggleDrawer(false)} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Drawer Menu */}
          <List className="flex-1 p-4">
            {menuItems.map((menu) => (
              <ListItem
                key={menu.label}
                component={Link}
                to={menu.path}
                onClick={toggleDrawer(false)}
                className={`mb-2 rounded-lg transition-all duration-300 hover:bg-white/10
                  ${isActive(menu.path) ? 'bg-white/20' : ''}`}
                sx={{ color: 'white' }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  <KeyboardReturnIcon className="transform scale-x-[-1]" />
                </ListItemIcon>
                <ListItemText 
                  primary={menu.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(menu.path) ? 600 : 400,
                  }}
                />
              </ListItem>
            ))}
          </List>

          {/* Drawer Footer */}
          <div className="p-4 border-t border-white/20">
            <p className="text-white text-sm text-center mb-2">24/7 Emergency</p>
            <a 
              href="tel:+447379787766"
              className="block w-full bg-white text-primary-600 text-center py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors duration-300"
            >
              📞 07379 787766
            </a>
          </div>
        </Box>
      </Drawer>
    </header>
  );
};

export default Header;
